import {
  ApiRef,
  DiscoveryApi,
  FetchApi,
  createApiRef,
} from '@backstage/frontend-plugin-api';
import {
  RbacEvaluateRequest,
  RbacEvaluateResponse,
  RbacMatchedStatement,
  RbacPolicyEntity,
  RbacRoleBindingEntity,
  RbacRoleEntity,
} from '@internal/plugin-rbac-common';

export type RbacRoleDetail = {
  role: RbacRoleEntity;
  effectiveStatements: RbacMatchedStatement[];
};

export interface RbacApi {
  listRoles(): Promise<RbacRoleEntity[]>;
  getRole(namespace: string, name: string): Promise<RbacRoleDetail>;
  listPolicies(): Promise<RbacPolicyEntity[]>;
  listBindings(): Promise<RbacRoleBindingEntity[]>;
  evaluate(request: RbacEvaluateRequest): Promise<RbacEvaluateResponse>;
}

export const rbacApiRef: ApiRef<RbacApi> = createApiRef<RbacApi>({
  id: 'plugin.rbac.service',
});

export class RbacClient implements RbacApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async listRoles(): Promise<RbacRoleEntity[]> {
    return this.getList<RbacRoleEntity>('/roles');
  }

  async getRole(namespace: string, name: string): Promise<RbacRoleDetail> {
    return this.request<RbacRoleDetail>(
      `/roles/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}`,
    );
  }

  async listPolicies(): Promise<RbacPolicyEntity[]> {
    return this.getList<RbacPolicyEntity>('/policies');
  }

  async listBindings(): Promise<RbacRoleBindingEntity[]> {
    return this.getList<RbacRoleBindingEntity>('/bindings');
  }

  async evaluate(request: RbacEvaluateRequest): Promise<RbacEvaluateResponse> {
    return this.request<RbacEvaluateResponse>('/evaluate', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
    });
  }

  private async getList<T>(path: string): Promise<T[]> {
    const response = await this.request<{ items: T[] }>(path);
    return response.items;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('rbac');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json() as Promise<T>;
  }
}
